import React, { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import ReservationsChart from './ReservationsChart';
import DeclineReasonsChart from './DeclineReasonsChart';
import PlayersChart from './PlayersChart';
import BusyHoursHeatmap from './BusyHoursHeatmap';
import { useDeclineReasonOptions } from './useDeclineReasonOptions';
import { Game } from '../../types';
import { COMMON_TRANSLATE_KEYS } from '../../constants/translate-keys';
import {
  BucketWeather,
  countPlayersByCategory,
  getBucketWeather,
  getBusyHours,
  getDeclineReasons,
  getPlayedPlayerKeys,
  getTimeBuckets,
  StatisticsPeriod,
} from '../../utils/statistics';

interface StatisticsSectionProps {
  /** Whether the games are of the whole complex or of one field. */
  scope: 'complex' | 'field';
  /** Shown as the section heading. */
  title?: string;
  /** Games in the selected period. */
  games: Game[];
  /** Games before the selected period, to find regular players. */
  previousGames: Game[];
  period: StatisticsPeriod;
  openingHours?: string;
  closingHours?: string;
  /** Daily weather keyed by YYYY-MM-DD, if the complex has a location. */
  dailyWeather?: Map<string, BucketWeather>;
  /** Extra content shown after the reservations chart. */
  children?: ReactNode;
}

const StatisticsSection = ({
  scope,
  title,
  games,
  previousGames,
  period,
  openingHours,
  closingHours,
  dailyWeather,
  children,
}: StatisticsSectionProps) => {
  const { t } = useTranslation();
  const declineReasonOptions = useDeclineReasonOptions();

  const knownPlayerKeys = useMemo(
    () => getPlayedPlayerKeys(previousGames),
    [previousGames]
  );
  const buckets = useMemo(
    () => getTimeBuckets(games, period, knownPlayerKeys),
    [games, period, knownPlayerKeys]
  );
  const weather = useMemo(
    () => (dailyWeather ? getBucketWeather(buckets, dailyWeather) : undefined),
    [buckets, dailyWeather]
  );
  const playerTotals = useMemo(
    () => countPlayersByCategory(games, knownPlayerKeys),
    [games, knownPlayerKeys]
  );
  const declineReasons = useMemo(
    () => getDeclineReasons(games, declineReasonOptions),
    [games, declineReasonOptions]
  );
  const busyHours = useMemo(
    () => getBusyHours(games, openingHours, closingHours),
    [games, openingHours, closingHours]
  );

  return (
    <section className="flex flex-col gap-4">
      {title && (
        <h2 className="text-mainWhite text-2xl font-bold mt-6">{title}</h2>
      )}
      <ReservationsChart buckets={buckets} weather={weather} />
      {children}
      <div className="grid xl:grid-cols-2 grid-cols-1 gap-4">
        <DeclineReasonsChart reasons={declineReasons} />
        <PlayersChart
          title={t(
            scope === 'complex'
              ? COMMON_TRANSLATE_KEYS.STATS_PLAYERS_VISITED_COMPLEX
              : COMMON_TRANSLATE_KEYS.STATS_PLAYERS_VISITED_FIELD
          )}
          subtitle={t(
            scope === 'complex'
              ? COMMON_TRANSLATE_KEYS.STATS_PLAYERS_CHART_HINT_COMPLEX
              : COMMON_TRANSLATE_KEYS.STATS_PLAYERS_CHART_HINT_FIELD
          )}
          buckets={buckets}
          totals={playerTotals}
        />
      </div>
      <BusyHoursHeatmap hours={busyHours.hours} cells={busyHours.cells} />
    </section>
  );
};

export default StatisticsSection;
